import React, { useState } from 'react';
import classes from './ResourceSelection.module.scss';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import { ResourceSelectionProps } from 'new/elements/ResourceSelectionButton/types';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { ReactComponent as CloseIcon } from 'new/components/icons/close-cross.svg';
import { resourceSearchView } from 'new/elements/ResourceSelectionButton/consts';
import { AvailableFilterTypesByView } from 'js/constants/visibilityFilters';
import { getResourceMeta } from 'js/hooks/useResourceMeta';
import usePaging from 'new/state/paging/repository/usePaging';
import useEntities from 'new/state/entities/repository/useEntities';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_ATTR_TRANSFORMS_REQUEST,
  GET_CREDENTIALS_REQUEST,
  GET_DATA_MAPS_REQUEST,
  GET_DATA_SETS_REQUEST,
  GET_DATA_SOURCES_REQUEST,
  GET_REUSABLE_RECORD_TRANSFORMS_REQUEST,
  GET_SINKS_REQUEST,
} from 'js/constants/actionTypes';
import useListingItems from 'new/elements/ResourceSelectionButton/components/useListingItems';
import {
  ResourceSelectionItem,
  ResourceSelectionItemProps,
} from 'new/elements/ResourceSelectionButton/components/ResourceSelectionItem';
import DataCardList from 'new/elements/DataCardList/DataCardList';
import { Button } from 'new/elements/Button';

export const ResourceSelection = (
  props: ResourceSelectionProps &
    Pick<
      ResourceSelectionItemProps,
      | 'previewActionLabel'
      | 'hidePreviewAction'
      | 'onClickPreview'
      | 'ExpandView'
      | 'additionalActions'
    >,
) => {
  const {
    resourceName,
    onClose,
    hideDetails,
    isItemDisabled = () => false,
    resourceType,
    multi,
    dataFeedCompatible,
    defaultSelected = [],
  } = props;
  const searchView = resourceSearchView[resourceType];
  const { paging } = usePaging();
  const { metaInfoById } = paging;
  const { data: entities } = useEntities();
  const { data, loadData } = useListingItems(resourceType, dataFeedCompatible);
  const [selected, setSelected] = useState(defaultSelected);

  const loading = useIsLoading(
    GET_DATA_SOURCES_REQUEST,
    GET_SINKS_REQUEST,
    GET_DATA_SETS_REQUEST,
    GET_DATA_MAPS_REQUEST,
    GET_REUSABLE_RECORD_TRANSFORMS_REQUEST,
    GET_ATTR_TRANSFORMS_REQUEST,
    GET_CREDENTIALS_REQUEST,
  );

  const onSelect = (resource: { id: any }, checked: boolean) => {
    if (checked) {
      if (multi) {
        setSelected([...selected, resource]);
      } else {
        setSelected([resource]);
      }
    } else {
      if (multi) {
        const newSelected = selected.filter((s) => s.id !== resource.id);
        setSelected(newSelected);
      } else {
        setSelected([]);
      }
    }
  };

  const onChoose = () => {
    props.onChoose(selected);
  };

  const rowRenderer = (index, item) => {
    return (
      <ResourceSelectionItem
        item={item}
        multi={props.multi}
        disabled={isItemDisabled(item)}
        onSelect={onSelect}
        resourceName={resourceName}
        selected={selected}
        hideDetails={hideDetails}
        previewActionLabel={props.previewActionLabel}
        ExpandView={props.ExpandView}
        onClickPreview={props.onClickPreview}
        hidePreviewAction={props.hidePreviewAction}
        additionalActions={props.additionalActions}
        getSourceMeta={getResourceMeta({
          metaInfoById,
          entities,
          resourceType,
          data_credentials: null,
        })}
      />
    );
  };

  return (
    <Stack className={classes.container}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        padding="16px"
        sx={{
          background: 'var(--color-greyscale-50)',
        }}
      >
        <Typography variant="subheading">Select a {resourceName}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <DataCardList
        data={data}
        inModal
        view={searchView}
        disablePageHistoryUpdate
        preventSearchFiltersUpdateFromUrlParams
        availableFilterTypes={AvailableFilterTypesByView[searchView]}
        loading={loading}
        rowRenderer={rowRenderer}
        getSourceMeta={getResourceMeta({
          metaInfoById,
          entities,
          resourceType,
          data_credentials: null,
        })}
        noContentView={null}
        loadData={loadData}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        padding="16px"
        sx={{
          borderTop: '1px solid var(--color-outline)',
        }}
      >
        <Button variant="outlined" color="basic" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={onChoose} disabled={!selected.length} variant="outlined" color="primary">
          Choose{multi ? ` ${selected.length}` : ''}
        </Button>
      </Stack>
    </Stack>
  );
};
